import { customerHubDate } from "./customerHubNavigation"
import { healthSourceLabel } from "./healthPresentation"

const SOURCE_LABELS = { roody_v1: "Roody", roody_v2: "Roody", group_fitness: "Groupfitness", health_connect: "Health Connect", huawei_health: "Huawei Health", apple_health: "Apple Health" }
const SOURCE_PRIORITY = { group_fitness: 0, roody_v2: 1, roody_v1: 2, health_connect: 3, huawei_health: 4 }
const safeSeconds = value => ["number", "string"].includes(typeof value) && value !== "" && Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : null
const unique = values => [...new Set(values.filter(Boolean))]

export function hubDay(value = new Date()) {
  if (customerHubDate(value)) return value
  const date = new Date(value)
  return Number.isFinite(date.getTime()) ? new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Zurich", year: "numeric", month: "2-digit", day: "2-digit" }).format(date) : ""
}

export function moveHubDay(day, offset) {
  if (!customerHubDate(day)) return ""
  const date = new Date(`${day}T12:00:00Z`)
  date.setUTCDate(date.getUTCDate() + offset)
  return date.toISOString().slice(0, 10)
}

export function hubPeriod(period = "month", anchor = hubDay()) {
  anchor = customerHubDate(anchor) || hubDay()
  const date = new Date(`${anchor}T12:00:00Z`)
  if (period === "all") return { period, anchor, from: "", to: "", label: "Gesamtverlauf" }
  if (period === "week") {
    const from = moveHubDay(anchor, -((date.getUTCDay() + 6) % 7)), to = moveHubDay(from, 6)
    return { period, anchor, from, to, label: `${from.slice(8)}.${from.slice(5, 7)}. – ${to.slice(8)}.${to.slice(5, 7)}.${to.slice(0, 4)}` }
  }
  if (period === "year") return { period, anchor, from: `${anchor.slice(0, 4)}-01-01`, to: `${anchor.slice(0, 4)}-12-31`, label: anchor.slice(0, 4) }
  const from = `${anchor.slice(0, 7)}-01`
  date.setUTCMonth(date.getUTCMonth() + 1, 0)
  return { period: "month", anchor, from, to: date.toISOString().slice(0, 10), label: new Intl.DateTimeFormat("de-CH", { month: "long", year: "numeric", timeZone: "UTC" }).format(date) }
}

export function hubSport(value) {
  const text = `${value?.activityFamily || ""} ${value?.activityName || ""} ${value?.title || ""}`.toLowerCase()
  if (/yoga/.test(text)) return { family: "yoga", label: "Yoga", icon: "mdi-yoga" }
  if (/pilates/.test(text)) return { family: "pilates", label: "Pilates", icon: "mdi-human-handsup" }
  if (/swim|schwimm|aqua/.test(text)) return { family: "swimming", label: "Schwimmen", icon: "mdi-swim" }
  if (/cycl|bike|radfahr|cycling|spinning/.test(text)) return { family: "cycling", label: "Radfahren", icon: "mdi-bike" }
  if (/running|\brun\b|laufen|lauftraining|jogg/.test(text)) return { family: "running", label: "Laufen", icon: "mdi-run" }
  if (/walk|wandern|gehen|hiking/.test(text)) return { family: "walking", label: "Gehen", icon: "mdi-walk" }
  if (/strength|kraft|weight|bodypump|muskel/.test(text)) return { family: "strength", label: "Krafttraining", icon: "mdi-dumbbell" }
  if (/hyrox|mixed|hiit|cross/.test(text)) return { family: "mixed", label: "Gemischtes Training", icon: "mdi-lightning-bolt-outline" }
  if (value?.kind === "course" || /group_fitness/.test(text)) return { family: "group_fitness", label: "Kurs", icon: "mdi-account-group-outline" }
  return { family: value?.activityFamily || "other", label: "Training", icon: "mdi-motion-play-outline" }
}

function normalizeServer(item) {
  if (!item?.id || !customerHubDate(item.date)) return null
  const activity = { ...item, durationSeconds: safeSeconds(item.durationSeconds), sourceName: SOURCE_LABELS[item.source] || "Roody",
    sourceIds: unique([item.id, ...(item.sourceIds || [])]), sources: [item.source], status: item.status || "unconfirmed", localHealth: false }
  return { ...activity, ...hubSport(activity) }
}

function normalizeHealth(workout) {
  if (!workout?.workoutId || !workout.provider) return null
  const start = Date.parse(workout.startedAt), end = Date.parse(workout.endedAt)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null
  const id = `health:${workout.provider}:${workout.workoutId}`
  const activity = { id, kind: "workout", source: workout.provider, sources: [workout.provider], sourceIds: [id],
    sourceName: healthSourceLabel(workout), title: workout.activityName || workout.title || "Health-Training",
    activityFamily: workout.activityFamily || "other", date: hubDay(start), startedAt: workout.startedAt, endedAt: workout.endedAt,
    timeIsExact: true, durationSeconds: safeSeconds(workout.durationSeconds), durationSource: safeSeconds(workout.durationSeconds) ? "recorded" : "unknown",
    status: "completed", healthLinks: [{ provider: workout.provider, workoutId: workout.workoutId }],
    linkedExecutionId: String(workout.existingLink?.executionId || ""), localHealth: true }
  return { ...activity, ...hubSport(activity) }
}

function exactKeys(activity) {
  const keys = [`id:${activity.id}`]
  if (activity.executionId) keys.push(`execution:${activity.executionId}`)
  if (activity.linkedExecutionId) keys.push(`execution:${activity.linkedExecutionId}`)
  if (activity.courseExecutionId) keys.push(`course:${activity.courseExecutionId}`)
  if (activity.clientSessionId) keys.push(`session:${activity.clientSessionId}`)
  const links = [...(activity.healthLinks || []), activity.healthImport].filter(Boolean)
  links.forEach(link => { if (link.provider && link.workoutId) keys.push(`health:${link.provider}:${link.workoutId}`) })
  return unique(keys)
}

function combine(group, resolution = "exact") {
  const sorted = [...group].sort((a, b) => (SOURCE_PRIORITY[a.source] ?? 9) - (SOURCE_PRIORITY[b.source] ?? 9))
  const primary = sorted[0]
  const instructed = sorted.some(item => item.status === "instructed")
  const completed = sorted.some(item => item.status === "completed")
  const duration = sorted.find(item => item.durationSource === "recorded" && item.durationSeconds !== null)
    || sorted.find(item => item.durationSeconds !== null)
  const timing = sorted.find(item => item.timeIsExact) || primary
  const execution = sorted.find(item => item.executionId)
  return { ...primary, status: instructed ? "instructed" : completed ? "completed" : primary.status,
    executionId: execution?.executionId || null, executionKind: execution?.executionKind || null,
    startedAt: timing.startedAt, endedAt: timing.endedAt, timeIsExact: timing.timeIsExact,
    durationSeconds: duration?.durationSeconds ?? null, durationSource: duration?.durationSource || "unknown",
    durationSourceName: duration?.sourceName || null,
    sourceIds: unique(sorted.flatMap(item => item.sourceIds)), sources: unique(sorted.flatMap(item => item.sources)),
    sourceNames: unique(sorted.map(item => item.sourceName)),
    members: sorted, resolution, pendingOverlap: false, overlapCandidates: [],
    healthLinks: sorted.flatMap(item => [...(item.healthLinks || []), item.healthImport].filter(Boolean)),
  }
}

function possibleOverlap(a, b) {
  if (a.status !== "completed" || b.status !== "completed") return false
  // Same-source entries with different IDs may be deliberately separate sessions.
  if (a.sources.length === 1 && b.sources.length === 1 && a.source === b.source && !a.localHealth && !b.localHealth) return false
  const aStart = Date.parse(a.startedAt), aEnd = Date.parse(a.endedAt), bStart = Date.parse(b.startedAt), bEnd = Date.parse(b.endedAt)
  if ([aStart, aEnd, bStart, bEnd].every(Number.isFinite) && aEnd > aStart && bEnd > bStart) {
    const overlap = Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart))
    // A common time window is a question, never a proof of an identical workout.
    if (overlap / Math.min(aEnd - aStart, bEnd - bStart) >= .5) return true
    if ((a.timeIsExact || a.scheduledTimeIsExact) && (b.timeIsExact || b.scheduledTimeIsExact)) return false
  }
  return a.date === b.date && (!a.timeIsExact || !b.timeIsExact) && a.family !== "other" && a.family === b.family &&
    a.durationSeconds !== null && b.durationSeconds !== null && Math.abs(a.durationSeconds - b.durationSeconds) <= 300
}

export function buildHubActivities(serverActivities = [], healthWorkouts = [], decisions = {}) {
  const rows = [...(Array.isArray(serverActivities) ? serverActivities : []).map(normalizeServer), ...(Array.isArray(healthWorkouts) ? healthWorkouts : []).map(normalizeHealth)].filter(Boolean)
  decisions = decisions && typeof decisions === "object" && !Array.isArray(decisions) ? decisions : {}
  const parents = rows.map((_, i) => i), keys = new Map()
  const root = index => { while (parents[index] !== index) index = parents[index]; return index }
  rows.forEach((row, i) => exactKeys(row).forEach(key => {
    if (keys.has(key)) parents[root(i)] = root(keys.get(key))
    else keys.set(key, i)
  }))
  const groups = new Map()
  rows.forEach((row, i) => { const key = root(i); if (!groups.has(key)) groups.set(key, []); groups.get(key).push(row) })
  let activities = [...groups.values()].map(group => combine(group))
    .sort((a, b) => (SOURCE_PRIORITY[a.source] ?? 9) - (SOURCE_PRIORITY[b.source] ?? 9) || a.id.localeCompare(b.id))
  // A local decision stores IDs only. It cannot grant rights or alter source records.
  const byId = new Map(), manualParents = new Map(activities.map(activity => [activity.id, activity.id]))
  const manualRoot = id => { while (manualParents.get(id) !== id) id = manualParents.get(id); return id }
  activities.forEach(activity => activity.sourceIds.forEach(sourceId => byId.set(sourceId, activity)))
  Object.entries(decisions).sort(([a], [b]) => a.localeCompare(b)).forEach(([sourceId, decision]) => {
    if (decision?.action !== "merge" || typeof decision.targetId !== "string") return
    const source = byId.get(sourceId), target = byId.get(decision.targetId)
    if (!source || !target || source === target || !possibleOverlap(source, target)) return
    const from = manualRoot(source.id), to = manualRoot(target.id)
    if (from !== to) manualParents.set(from, to)
  })
  const manualGroups = new Map()
  activities.forEach(activity => {
    const key = manualRoot(activity.id)
    if (!manualGroups.has(key)) manualGroups.set(key, [])
    manualGroups.get(key).push(activity)
  })
  activities = [...manualGroups.entries()].map(([id, group]) => group.length === 1 ? group[0] : { ...combine(group.flatMap(item => item.members), "confirmed"), id })
  activities.forEach((activity, i) => {
    activity.overlapCandidates = activities.slice(0, i).filter(other => possibleOverlap(activity, other)).map(other => ({ id: other.id, title: other.title, sources: other.sourceNames }))
    const decision = decisions[activity.id]
    // A confirmation applies only to the candidates the customer actually saw.
    // A newly arriving source needs its own decision before it is counted twice.
    activity.separateConfirmed = decision?.action === "separate" && Array.isArray(decision.targetIds) &&
      activity.overlapCandidates.every(candidate => decision.targetIds.includes(candidate.id))
    activity.pendingOverlap = activity.overlapCandidates.length > 0 && !activity.separateConfirmed
  })
  return activities.sort((a, b) => b.date.localeCompare(a.date) || String(b.startedAt || "").localeCompare(String(a.startedAt || "")))
}

export function hubStatistics(activities, checkins = [], range = {}) {
  const inside = item => (!range.from || item.date >= range.from) && (!range.to || item.date <= range.to)
  const selected = activities.filter(inside)
  const completed = selected.filter(item => item.status === "completed" && !item.pendingOverlap)
  const days = unique(completed.map(item => item.date)).sort()
  const visits = checkins.filter(inside)
  const sources = new Map(), timeSources = new Map()
  completed.forEach(item => {
    const label = SOURCE_LABELS[item.source] || item.sourceName
    if (!sources.has(label)) sources.set(label, { label, count: 0, durationSeconds: 0, unknownDuration: 0 })
    const source = sources.get(label)
    source.count++
    if (item.durationSeconds === null) source.unknownDuration++
    else source.durationSeconds += item.durationSeconds
    const timeLabel = item.durationSourceName || "Dauer unbekannt"
    if (!timeSources.has(timeLabel)) timeSources.set(timeLabel, { label: timeLabel, count: 0, durationSeconds: 0, unknownDuration: 0 })
    const timeSource = timeSources.get(timeLabel)
    timeSource.count++
    if (item.durationSeconds === null) timeSource.unknownDuration++
    else timeSource.durationSeconds += item.durationSeconds
  })
  return { count: completed.length, durationSeconds: completed.reduce((sum, item) => sum + (item.durationSeconds ?? 0), 0),
    activeDays: days.length, days, visits: visits.length, visitEntries: visits, completed,
    pending: selected.filter(item => item.pendingOverlap), instructed: selected.filter(item => item.status === "instructed"),
    unknownDuration: completed.filter(item => item.durationSeconds === null).length,
    scheduleDuration: completed.filter(item => item.durationSource === "course_schedule").length,
    sources: [...sources.values()], timeSources: [...timeSources.values()] }
}

export function hubDuration(seconds) {
  if (seconds === null || seconds === undefined || !Number.isFinite(Number(seconds))) return "Dauer unbekannt"
  const minutes = Math.floor(Number(seconds) / 60)
  if (seconds > 0 && minutes === 0) return "Unter 1 Min."
  return minutes >= 60 ? `${Math.floor(minutes / 60)} Std. ${minutes % 60} Min.` : `${minutes} Min.`
}

export function hubStatusLabel(status) {
  return { completed: "Absolviert", planned: "Gebucht", unconfirmed: "Teilnahme nicht bestätigt", instructed: "Kurs geleitet" }[status] || "Aktivität"
}
